import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: ReactNode;
  error?: ReactNode;
  inputSize?: 'sm' | 'md';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, inputSize = 'md', className, id, ...rest }, ref) => {
    const inputId = id ?? `input-${Math.random().toString(36).slice(2, 9)}`;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-text-primary">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined}
          className={cn(
            'w-full rounded-md border bg-bg-surface font-sans text-text-primary',
            'transition-[border-color,box-shadow] duration-fast',
            'placeholder:text-text-tertiary',
            'focus-visible:border-border-focus focus-visible:outline-none',
            inputSize === 'sm' ? 'h-9 px-3 text-sm' : 'h-11 px-3.5 text-base',
            hasError
              ? 'border-error-400 focus-visible:shadow-[0_0_0_3px_rgba(196,74,63,0.18)]'
              : 'border-border-default hover:border-border-strong focus-visible:shadow-[0_0_0_3px_rgba(217,119,87,0.18)]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          {...rest}
        />
        {hasError ? (
          <p id={`${inputId}-err`} className="mt-1.5 text-xs text-error-600" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-text-tertiary">
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';
